import React, { useState, useEffect, useRef } from 'react';
import './ExecutionCommon.css';

function LiveLogViewer({ executionId, history, isPolling }) {
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [autoScroll, setAutoScroll] = useState(true);
  const [expandedEntries, setExpandedEntries] = useState(new Set());
  const logContainerRef = useRef(null);

  // Auto-scroll to bottom when new entries arrive
  useEffect(() => {
    if (autoScroll && logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [history, autoScroll]);

  // Filter events
  const filteredHistory = (history || []).filter(event => {
    // Action filter
    if (filter !== 'all') {
      if (filter === 'node' && !event.node_id) return false;
      if (filter === 'gate' && !event.gate_id) return false;
      if (filter === 'error' && !event.error) return false;
      if (filter === event.action && event.action !== filter) return false;
    }

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const nodeId = (event.node_id || '').toLowerCase();
      const action = (event.action || '').toLowerCase();
      const error = (event.error || '').toLowerCase();
      
      if (!nodeId.includes(searchLower) && 
          !action.includes(searchLower) && 
          !error.includes(searchLower)) {
        return false;
      }
    }

    return true;
  });

  // Toggle entry expansion
  const toggleExpand = (historyId) => {
    setExpandedEntries(prev => {
      const newSet = new Set(prev);
      if (newSet.has(historyId)) {
        newSet.delete(historyId);
      } else {
        newSet.add(historyId);
      }
      return newSet;
    });
  };

  // Format timestamp
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      fractionalSecondDigits: 3,
    });
  };

  // Get action color class
  const getActionClass = (action) => {
    switch (action) {
      case 'started': return 'action-started';
      case 'completed': return 'action-completed';
      case 'failed': return 'action-failed';
      case 'skipped': return 'action-skipped';
      case 'gate_fired': return 'action-gate';
      case 'paused': return 'action-paused';
      case 'resumed': return 'action-resumed';
      case 'cancelled': return 'action-cancelled';
      default: return '';
    }
  };

  // Export logs
  const handleExport = (format) => {
    const data = format === 'json' 
      ? JSON.stringify(filteredHistory, null, 2)
      : filteredHistory.map(e => 
          `[${formatTime(e.timestamp)}] ${e.action.toUpperCase().padEnd(12)} ${e.node_id || e.gate_id || 'PROCESS'} ${e.duration_ms ? `(${e.duration_ms}ms)` : ''} ${e.error || ''}`
        ).join('\n');

    const blob = new Blob([data], { type: format === 'json' ? 'application/json' : 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `execution-log-${executionId || 'unknown'}.${format === 'json' ? 'json' : 'txt'}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!executionId) {
    return (
      <div className="live-log-viewer empty">
        <div className="empty-state">
          <div className="empty-icon">📜</div>
          <h3>No Execution Log</h3>
          <p>Start an execution to see the live log</p>
        </div>
      </div>
    );
  }

  return (
    <div className="live-log-viewer">
      {/* Toolbar */}
      <div className="log-toolbar">
        <div className="toolbar-left">
          <div className="filter-group">
            <label>Filter:</label>
            <select value={filter} onChange={(e) => setFilter(e.target.value)}>
              <option value="all">All Events</option>
              <option value="node">Node Events</option>
              <option value="gate">Gate Events</option>
              <option value="error">Errors Only</option>
              <option value="started">Started</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
            </select>
          </div>
          <div className="search-group">
            <input
              type="text"
              placeholder="Search logs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        <div className="toolbar-right">
          {isPolling && (
            <span className="polling-indicator">
              <span className="polling-dot"></span>
              Live
            </span>
          )}
          <label className="auto-scroll-toggle">
            <input
              type="checkbox"
              checked={autoScroll}
              onChange={(e) => setAutoScroll(e.target.checked)}
            />
            Auto-scroll
          </label>
          <button className="export-btn" onClick={() => handleExport('txt')}>
            Export TXT
          </button>
          <button className="export-btn" onClick={() => handleExport('json')}>
            Export JSON
          </button>
        </div>
      </div>

      {/* Log Entries */}
      <div className="log-container" ref={logContainerRef}>
        {filteredHistory.length === 0 ? (
          <div className="no-logs">
            {history?.length > 0 
              ? 'No entries match the current filter'
              : 'Waiting for events...'}
          </div>
        ) : (
          filteredHistory.map((event, idx) => {
            const isExpanded = expandedEntries.has(event.history_id || idx);
            const hasDetails = event.tokens_used || event.error || event.duration_ms
              || event.inputs_used || event.outputs_produced;

            return (
              <div 
                key={event.history_id || idx} 
                className={`log-entry ${getActionClass(event.action)} ${isExpanded ? 'expanded' : ''}`}
                onClick={() => hasDetails && toggleExpand(event.history_id || idx)}
              >
                <div className="log-main">
                  <span className="log-time">[{formatTime(event.timestamp)}]</span>
                  <span className={`log-action ${getActionClass(event.action)}`}>
                    {event.action.toUpperCase()}
                  </span>
                  <span className="log-target">
                    {event.node_id || event.gate_id || 'PROCESS'}
                  </span>
                  {event.duration_ms && (
                    <span className="log-duration">{event.duration_ms}ms</span>
                  )}
                  {event.tokens_used && (
                    <span className="log-tokens">{event.tokens_used} tokens</span>
                  )}
                  {hasDetails && (
                    <span className="expand-icon">{isExpanded ? '▼' : '▶'}</span>
                  )}
                </div>
                
                {event.error && (
                  <div className="log-error">
                    {event.error_type && <span className="error-type">[{event.error_type}]</span>}
                    {event.error}
                  </div>
                )}

                {isExpanded && (
                  <div className="log-details">
                    {event.tokens_used && (
                      <div className="detail-row">
                        <span className="detail-label">Tokens:</span>
                        <span className="detail-value">
                          Total: {event.tokens_used} | 
                          Prompt: {event.prompt_tokens || 0} | 
                          Completion: {event.completion_tokens || 0}
                        </span>
                      </div>
                    )}
                    {event.llm_model && (
                      <div className="detail-row">
                        <span className="detail-label">Model:</span>
                        <span className="detail-value">{event.llm_model}</span>
                      </div>
                    )}
                    {event.retry_count > 0 && (
                      <div className="detail-row">
                        <span className="detail-label">Retries:</span>
                        <span className="detail-value">{event.retry_count}</span>
                      </div>
                    )}
                    {event.inputs_used && Object.keys(event.inputs_used).length > 0 && (
                      <div className="detail-row">
                        <span className="detail-label">Inputs:</span>
                        <pre className="detail-json">
                          {JSON.stringify(event.inputs_used, null, 2)}
                        </pre>
                      </div>
                    )}
                    {event.outputs_produced && Object.keys(event.outputs_produced).length > 0 && (
                      <div className="detail-row">
                        <span className="detail-label">Outputs:</span>
                        <pre className="detail-json">
                          {JSON.stringify(event.outputs_produced, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Status Bar */}
      <div className="log-status-bar">
        <span>{filteredHistory.length} events</span>
        {history && history.length !== filteredHistory.length && (
          <span>({history.length} total)</span>
        )}
      </div>
    </div>
  );
}

export default LiveLogViewer;
